import type { TemplateTask } from '@/types/checklist-template';
import { TaskCategory, TimingAnchor } from '@/types/task';

const MONTH_ABBR = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatTimingDescription(task: TemplateTask): string {
  switch (task.timingAnchor) {
    case TimingAnchor.OpenerRelative: {
      const d = task.offsetDays;
      if (d === 0) return 'On opener day';
      if (d < 0) return `${Math.abs(d)} days before opener`;
      return `${d} days after opener`;
    }
    case TimingAnchor.CalendarDate: {
      if (task.calendarMonth === undefined) return 'Calendar date TBD';
      const mon = MONTH_ABBR[task.calendarMonth] ?? '';
      const start = task.calendarDayRangeStart;
      const end = task.calendarDayRangeEnd;
      if (start !== undefined && end !== undefined) return `${mon} ${start}–${end}`;
      if (start !== undefined) return `${mon} ${start}`;
      return mon;
    }
    case TimingAnchor.FrostDate:
      return 'Around frost date';
    case TimingAnchor.RutRelative:
      return 'Around rut peak';
    default:
      return 'TBD';
  }
}

const CATEGORY_LABELS: Record<TaskCategory, string> = {
  [TaskCategory.FoodSource]: 'Food Source',
  [TaskCategory.StandPrep]: 'Stand Prep',
  [TaskCategory.TrailCameras]: 'Trail Cameras',
  [TaskCategory.Scouting]: 'Scouting',
  [TaskCategory.GearEquipment]: 'Gear & Equipment',
  [TaskCategory.PropertyInfrastructure]: 'Property Infrastructure',
  [TaskCategory.HabitatManagement]: 'Habitat Management',
  [TaskCategory.RegulatoryAdmin]: 'Regulatory & Admin',
  [TaskCategory.PostSeason]: 'Post-Season',
  [TaskCategory.PredatorControl]: 'Predator Control',
  [TaskCategory.WaterholeManagement]: 'Waterhole Management',
  [TaskCategory.WildlifeStructures]: 'Wildlife Structures',
  [TaskCategory.MineralNutrition]: 'Mineral & Nutrition',
  [TaskCategory.NeighborAccess]: 'Neighbor & Access',
  [TaskCategory.HerdData]: 'Herd Data',
  [TaskCategory.MockScrapes]: 'Mock Scrapes',
  [TaskCategory.TreePlanting]: 'Tree Planting',
  [TaskCategory.Other]: 'Other',
};

export function categoryLabel(category: TaskCategory): string {
  return CATEGORY_LABELS[category] ?? category;
}

export interface CategoryGroup {
  category: TaskCategory;
  label: string;
  tasks: TemplateTask[];
}

export function groupTasksByCategory(tasks: TemplateTask[]): CategoryGroup[] {
  const map = new Map<TaskCategory, TemplateTask[]>();
  for (const task of tasks) {
    const arr = map.get(task.category);
    if (arr) {
      arr.push(task);
    } else {
      map.set(task.category, [task]);
    }
  }
  return Array.from(map.entries()).map(([category, catTasks]) => ({
    category,
    label: categoryLabel(category),
    tasks: catTasks,
  }));
}
